/**
 * MCPConnectWizard — step-by-step provider connection guide.
 *
 * Reuses the existing OAuth flow — does NOT duplicate auth. The wizard is
 * pure presentation: it shows the user the exact URL + steps to add this
 * tenant as an MCP server inside ChatGPT / Claude / Gemini, then waits
 * passively for the provider's OAuth callback to land (which is handled
 * by the existing /api/oauth/authorize endpoint).
 *
 * UX additions (Feb 2026):
 *  - One-tap "Copy URL & Open <Provider>" button (clipboard + window.open
 *    in the same user gesture — the only cross-origin automation browsers
 *    permit).
 *  - Provider-specific deep links where they exist:
 *      Claude:  https://claude.ai/settings/connectors        (confirmed)
 *      ChatGPT: https://chatgpt.com/#settings                (settings drawer)
 *      Gemini:  no consumer-web MCP UI yet — falls back to docs.
 *  - Large primary "Copy MCP server URL" button with copied state.
 *  - Inline visual mockup illustrations (CSS/SVG) for each step.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check, Copy, ExternalLink, ClipboardCheck, AlertTriangle, Shield, MousePointerClick, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MCP_URL = `${process.env.REACT_APP_BACKEND_URL?.includes('emergentagent.com') ? 'https://getrestaff.co' : (process.env.REACT_APP_BACKEND_URL || 'https://getrestaff.co')}/api/mcp`;

const PROVIDERS = {
  chatgpt: {
    name: 'ChatGPT',
    color: 'emerald',
    // No stable deep link to Connectors sub-tab — best we can do is open
    // the Settings drawer. User taps "Connectors" once they land.
    deepLink: 'https://chatgpt.com/#settings',
    deepLinkLabel: 'Open ChatGPT Settings',
    deepLinkHint: 'Opens ChatGPT Settings. Click "Connectors" (or "Apps & Connectors") in the left rail, then "Add custom MCP server".',
    requires: 'Requires ChatGPT Pro/Business with Developer mode enabled in Settings → Connectors.',
    finalNote: 'After approval, return to chatgpt.com — the connector shows Connected.',
  },
  claude: {
    name: 'Claude',
    color: 'orange',
    // Confirmed stable deep link
    deepLink: 'https://claude.ai/settings/connectors',
    deepLinkLabel: 'Open Claude Connectors',
    deepLinkHint: 'Opens Claude\'s Connectors page directly. Click "Add custom connector", then paste the URL below.',
    requires: 'Available on Claude Pro, Max, Team & Enterprise plans.',
    finalNote: 'The new connector appears in Claude\'s sidebar — toggle it on per conversation via the + button.',
  },
  gemini: {
    name: 'Gemini',
    color: 'blue',
    // Gemini consumer web doesn't expose an MCP-server-add UI yet.
    deepLink: 'https://developer.android.com/studio/gemini/add-mcp-server',
    deepLinkLabel: 'View Gemini MCP guide',
    deepLinkHint: 'Gemini\'s consumer web app does not currently expose a custom MCP server UI. MCP is supported via Gemini CLI and Android Studio — the guide explains both.',
    requires: 'MCP support is in Gemini CLI / Android Studio today. Native web UI is on Google\'s roadmap.',
    finalNote: 'Once Gemini ships its consumer-web MCP UI, this URL is what you will paste — no changes needed on our side.',
    unsupportedWeb: true,
  },
};

const SCOPES = [
  { key: 'staff.read',     label: 'Read employee directory (no PII like password/PIN)' },
  { key: 'schedule.read',  label: 'Read schedules and assigned shifts' },
  { key: 'timesheet.read', label: 'Read timesheets and worked hours' },
  { key: 'leave.read',     label: 'Read leave balances and requests' },
  { key: 'analytics.read', label: 'Read aggregated labor cost and analytics' },
];

// --- Inline visual mockups (CSS/SVG, NOT real platform screenshots) ---
// Trademark-safe, won't break when ChatGPT/Claude/Gemini change their UI.

const MockBrowser = ({ children, urlText, dataTestid }) => (
  <div className="w-full max-w-full rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={dataTestid}>
    <div className="flex items-center gap-1.5 px-3 py-2 bg-slate-50 border-b border-slate-200">
      <span className="h-2.5 w-2.5 rounded-full bg-red-400 shrink-0" />
      <span className="h-2.5 w-2.5 rounded-full bg-amber-400 shrink-0" />
      <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shrink-0" />
      <div className="ml-2 flex-1 min-w-0 truncate text-[10px] font-mono text-slate-500 bg-white border border-slate-200 rounded px-2 py-0.5">
        {urlText}
      </div>
    </div>
    <div className="p-3 min-w-0">{children}</div>
  </div>
);

const Step1Mock = ({ provider }) => (
  <MockBrowser
    urlText={provider.deepLink.replace(/^https?:\/\//, '')}
    dataTestid="mcp-wizard-mock-step1"
  >
    <div className="flex gap-2">
      <div className="w-20 space-y-1 text-[10px] text-slate-400">
        <div className="px-1.5 py-1 rounded bg-slate-100">General</div>
        <div className="px-1.5 py-1 rounded bg-slate-900 text-white flex items-center gap-1">
          <span>Connectors</span>
        </div>
        <div className="px-1.5 py-1 rounded">Account</div>
      </div>
      <div className="flex-1 space-y-1.5">
        <div className="h-2 w-2/3 rounded bg-slate-200" />
        <div className="h-2 w-1/2 rounded bg-slate-100" />
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-medium bg-slate-900 text-white rounded px-2 py-1">
          <MousePointerClick size={10} /> Add custom MCP
        </div>
      </div>
    </div>
  </MockBrowser>
);

const Step2Mock = () => (
  <MockBrowser urlText="settings/connectors/new" dataTestid="mcp-wizard-mock-step2">
    <div className="space-y-2">
      <div className="text-[10px] text-slate-500">Server URL</div>
      <div className="border border-slate-300 rounded px-2 py-1.5 bg-slate-50 font-mono text-[10px] text-slate-700 break-all">
        {MCP_URL}
      </div>
      <div className="flex justify-end">
        <div className="inline-flex items-center gap-1 text-[10px] font-medium bg-slate-900 text-white rounded px-2 py-1">
          Add
        </div>
      </div>
    </div>
  </MockBrowser>
);

const Step3Mock = () => (
  <MockBrowser urlText="getrestaff.co/oauth/authorize" dataTestid="mcp-wizard-mock-step3">
    <div className="space-y-1.5">
      <div className="text-[10px] font-medium text-slate-700">ReStaff requests access:</div>
      {SCOPES.slice(0, 3).map(s => (
        <div key={s.key} className="flex items-center gap-1.5 text-[10px]">
          <Check size={10} className="text-emerald-600" />
          <code className="font-mono text-[10px] bg-slate-100 px-1 rounded">{s.key}</code>
        </div>
      ))}
      <div className="text-[10px] text-slate-400">+ 2 more…</div>
      <div className="flex justify-end gap-1.5 mt-2">
        <div className="text-[10px] border border-slate-300 rounded px-2 py-1">Deny</div>
        <div className="text-[10px] font-medium bg-emerald-600 text-white rounded px-2 py-1">Approve</div>
      </div>
    </div>
  </MockBrowser>
);

const Step4Mock = ({ provider }) => (
  <MockBrowser urlText="app.getrestaff.co/settings" dataTestid="mcp-wizard-mock-step4">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[10px] font-medium text-slate-700">{provider.name}</div>
        <div className="text-[10px] text-slate-400">5 scopes • just now</div>
      </div>
      <div className="text-[10px] font-medium bg-emerald-100 text-emerald-700 rounded px-2 py-0.5">
        Connected
      </div>
    </div>
  </MockBrowser>
);

export default function MCPConnectWizard({ open, providerId, polling = false, connected = false, timedOut = false, onClose }) {
  const [step, setStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [opened, setOpened] = useState(false);
  const provider = providerId ? PROVIDERS[providerId] : null;

  const reset = () => { setStep(1); setCopied(false); setOpened(false); onClose && onClose(); };

  // When the parent's poll loop detects the provider just flipped to connected,
  // auto-advance the wizard to Step 4 and toast a single success message.
  useEffect(() => {
    if (open && connected && step !== 4) {
      setStep(4);
      toast.success(`${provider?.name || 'Provider'} connected successfully`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, open]);

  const copyOnly = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      setCopied(true);
      toast.success('Server URL copied to clipboard');
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error('Could not copy — please long-press the URL above and copy manually.');
    }
  };

  // The ONE supported cross-origin "automation" pattern: in the same user
  // gesture, write to clipboard AND window.open the destination. Browsers
  // allow both because they originate from a trusted user click.
  const copyAndOpen = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      setCopied(true);
      setOpened(true);
      window.open(provider.deepLink, '_blank', 'noopener,noreferrer');
      toast.success(`URL copied — paste it in ${provider.name}`);
      setTimeout(() => { setCopied(false); setOpened(false); }, 4000);
    } catch (e) {
      // Even if clipboard fails (e.g., insecure context), still open the tab.
      window.open(provider.deepLink, '_blank', 'noopener,noreferrer');
      toast.warning(`Opened ${provider.name} — copy the URL manually below.`);
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); }}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-1 [&>*]:min-w-0"
        data-testid={`mcp-connect-wizard-${providerId}`}
      >
        <DialogHeader>
          <DialogTitle>Connect {provider.name} to ReStaff</DialogTitle>
          <DialogDescription>
            Read-only AI access via the Model Context Protocol — secure, scoped, and revocable in one click.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-3" data-testid="mcp-connect-wizard-stepper">
          {[1, 2, 3, 4].map((n) => (
            <React.Fragment key={n}>
              <div className={`h-6 w-6 rounded-full text-xs flex items-center justify-center ${n <= step ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {n < step ? <Check size={12} /> : n}
              </div>
              {n < 4 && <div className={`flex-1 h-0.5 ${n < step ? 'bg-slate-900' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Live connection banner — only visible while parent is actively polling. */}
        {connected ? (
          <div
            className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
            data-testid="mcp-wizard-live-connected"
          >
            <Check size={14} className="shrink-0" />
            <span>Connected successfully — you can close this window.</span>
          </div>
        ) : timedOut ? (
          <div
            className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="mcp-wizard-live-timeout"
          >
            <AlertTriangle size={14} className="shrink-0" />
            <span>Still waiting? Once you approve in {provider.name}, this row will refresh automatically the next time you open Settings.</span>
          </div>
        ) : polling ? (
          <div
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
            data-testid="mcp-wizard-live-polling"
          >
            <Loader2 size={14} className="shrink-0 animate-spin" />
            <span>Waiting for AI connection…</span>
          </div>
        ) : null}

        {/* --- STEP 1: Open provider --- */}
        {step === 1 && (
          <div className="space-y-3 text-sm">
            <div className="font-medium">Step 1 — Open {provider.name}</div>

            {provider.unsupportedWeb && (
              <div
                className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                data-testid="mcp-wizard-gemini-notice"
              >
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <div>
                  <strong>Heads up:</strong> Gemini's consumer web app doesn't expose a custom MCP server UI yet. Today, MCP works with <strong>Gemini CLI</strong> and <strong>Gemini in Android Studio</strong>. We'll surface the web option here as soon as Google ships it.
                </div>
              </div>
            )}

            <Step1Mock provider={provider} />

            <Button
              size="lg"
              className="w-full h-12 text-base"
              onClick={copyAndOpen}
              data-testid="mcp-wizard-copy-and-open"
            >
              {opened
                ? <><Check size={18} className="mr-2" /> URL copied · {provider.name} opened</>
                : <><ClipboardCheck size={18} className="mr-2" /> Copy URL &amp; Open {provider.name}</>}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              One tap — we copy the MCP URL to your clipboard <em>and</em> open {provider.name} in a new tab. Then just paste.
            </p>

            <div className="text-xs text-slate-600 border-t pt-3">
              {provider.deepLinkHint}
              <div className="mt-1.5 text-slate-500"><Shield size={11} className="inline mr-1" />{provider.requires}</div>
            </div>
          </div>
        )}

        {/* --- STEP 2: Paste server URL --- */}
        {step === 2 && (
          <div className="space-y-3 text-sm">
            <div className="font-medium">Step 2 — Paste this server URL</div>

            <Step2Mock />

            <div className="rounded-lg border-2 border-slate-300 bg-slate-50 p-3 space-y-2">
              <div className="text-xs font-medium text-slate-600 uppercase tracking-wide">MCP server URL</div>
              <code
                className="block font-mono text-xs sm:text-sm text-slate-900 break-all select-all"
                data-testid="mcp-connect-url"
              >
                {MCP_URL}
              </code>
            </div>

            <Button
              size="lg"
              variant={copied ? 'outline' : 'default'}
              className={`w-full h-12 text-base ${copied ? 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-50' : ''}`}
              onClick={copyOnly}
              data-testid="mcp-connect-copy-url"
            >
              {copied
                ? <><Check size={18} className="mr-2" /> Copied to clipboard</>
                : <><Copy size={18} className="mr-2" /> Copy MCP server URL</>}
            </Button>

            <p className="text-xs text-slate-500">
              Paste it into the "Server URL" field in {provider.name}. {provider.name} will then redirect you to ReStaff to sign in and approve scopes.
            </p>

            {!provider.unsupportedWeb && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => window.open(provider.deepLink, '_blank', 'noopener,noreferrer')}
                data-testid="mcp-wizard-open-provider"
              >
                <ExternalLink size={14} className="mr-1.5" /> Open {provider.name} in new tab
              </Button>
            )}
          </div>
        )}

        {/* --- STEP 3: Approve scopes --- */}
        {step === 3 && (
          <div className="space-y-3 text-sm">
            <div className="font-medium">Step 3 — Approve the scopes you want to grant</div>

            <Step3Mock />

            <p className="text-slate-600">
              {provider.name} will redirect to ReStaff. Sign in (same login you're using now), then approve the read-only scopes below. Each is independent — uncheck what you don't want to share.
            </p>
            <ul className="space-y-1.5">
              {SCOPES.map(s => (
                <li key={s.key} className="flex items-start gap-2">
                  <Check size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <code className="font-mono text-xs bg-slate-100 px-1 rounded">{s.key}</code>
                    <span className="text-slate-600 text-xs ml-2">{s.label}</span>
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500">No write scopes are offered. AI cannot modify or delete any data.</p>
          </div>
        )}

        {/* --- STEP 4: Confirm connected --- */}
        {step === 4 && (
          <div className="space-y-3 text-sm">
            <div className="font-medium flex items-center gap-2">
              <Check size={16} className="text-emerald-600" /> Step 4 — Confirm in this dashboard
            </div>

            <Step4Mock provider={provider} />

            <p className="text-slate-600">{provider.finalNote}</p>
            <p className="text-slate-600">
              The row for <strong>{provider.name}</strong> above will show a green <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Connected</Badge> badge with the scopes you granted and the last-used timestamp.
            </p>
            <p className="text-xs text-slate-500">
              Need to revoke later? Click <strong>Disconnect</strong> on the row — all active tokens for {provider.name} are revoked instantly.
            </p>
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
          <Button variant="outline" size="sm" onClick={reset} data-testid="mcp-connect-wizard-close">
            Close
          </Button>
          <div className="flex gap-2">
            {step > 1 && (
              <Button variant="outline" size="sm" onClick={() => setStep(step - 1)} data-testid="mcp-connect-wizard-back">
                Back
              </Button>
            )}
            {step < 4 ? (
              <Button size="sm" onClick={() => setStep(step + 1)} data-testid="mcp-connect-wizard-next">
                Next
              </Button>
            ) : (
              <Button size="sm" onClick={reset} data-testid="mcp-connect-wizard-done">
                Done
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { ExternalLink };
